import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  Select,
  Option,
  Stack,
  Typography,
  Alert,
  Divider,
  Chip,
} from '@mui/joy';
import { ContentCopy, CheckCircle, Warning } from '@mui/icons-material';
import { scheduleService } from '@/services/scheduleService';
import { shiftTypeService } from '@/services/shiftTypeService';
import { userService } from '@/services/userService';
import { ShiftType } from '@/types/shiftType';
import { User } from '@/types/user';
import toast from 'react-hot-toast';

// Global cache for modal data to prevent excessive API calls
let cachedShiftTypes: ShiftType[] | null = null;
let cachedEmployees: User[] | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CopySchedulesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface CopyFormData {
  source_start_date: string;
  source_end_date: string;
  target_start_date: string;
  employee_id?: number;
  shift_type_id?: number;
  status?: 'draft' | 'published';
}

interface CopyFormErrors {
  source_start_date?: string;
  source_end_date?: string;
  target_start_date?: string;
  general?: string;
}

export default function CopySchedulesModal({ open, onClose, onSuccess }: CopySchedulesModalProps) {
  const [formData, setFormData] = useState<CopyFormData>({
    source_start_date: '',
    source_end_date: '',
    target_start_date: '',
  });
  const [errors, setErrors] = useState<CopyFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);
  const [copyResult, setCopyResult] = useState<{
    copied: number;
    conflicts: number;
    details: {
      copied: any[];
      conflicts: Array<{ id: number; employee_id: number; start_datetime: string; end_datetime: string }>;
    };
  } | null>(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Load shift types and employees on mount with caching
  useEffect(() => {
    const loadData = async () => {
      const now = Date.now();
      
      // Check if we have valid cached data
      if (cachedShiftTypes && cachedEmployees && (now - lastFetchTime) < CACHE_DURATION) {
        setShiftTypes(cachedShiftTypes);
        setEmployees(cachedEmployees.filter(u => u.role === 'employee' || u.role === 'manager'));
        return;
      }

      try {
        const [shiftTypesData, employeesData] = await Promise.all([
          shiftTypeService.getAllShiftTypes(),
          userService.getAllUsers()
        ]);
        
        // Only update state if component is still mounted
        if (mountedRef.current) {
          setShiftTypes(shiftTypesData);
          setEmployees(employeesData.filter(u => u.role === 'employee' || u.role === 'manager'));
          
          // Update cache
          cachedShiftTypes = shiftTypesData;
          cachedEmployees = employeesData;
          lastFetchTime = now;
        }
      } catch (error) {
        console.error('Error loading data for copy modal:', error);
        
        // Use cached data on error if available
        if (cachedShiftTypes && cachedEmployees && mountedRef.current) {
          setShiftTypes(cachedShiftTypes);
          setEmployees(cachedEmployees.filter(u => u.role === 'employee' || u.role === 'manager'));
        }
      }
    };
    
    loadData();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: CopyFormErrors = {};

    if (!formData.source_start_date) {
      newErrors.source_start_date = 'Source start date is required';
    }

    if (!formData.source_end_date) {
      newErrors.source_end_date = 'Source end date is required';
    }

    if (!formData.target_start_date) {
      newErrors.target_start_date = 'Target start date is required';
    }

    if (formData.source_start_date && formData.source_end_date) {
      const sourceStart = new Date(formData.source_start_date);
      const sourceEnd = new Date(formData.source_end_date);
      if (sourceStart >= sourceEnd) {
        newErrors.source_end_date = 'Source end date must be after source start date';
      }
    }

    if (formData.source_end_date && formData.target_start_date) {
      const sourceEnd = new Date(formData.source_end_date);
      const targetStart = new Date(formData.target_start_date);
      if (targetStart <= sourceEnd) {
        newErrors.target_start_date = 'Target start date must be after source end date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setCopyResult(null);

    try {
      const result = await scheduleService.copySchedules(formData);
      setCopyResult(result);
      
      if (result.copied > 0) {
        toast.success(`Successfully copied ${result.copied} schedules`);
        onSuccess?.();
      }
      
      if (result.conflicts > 0) {
        toast.error(`${result.conflicts} schedules had conflicts and were skipped`);
      }
    } catch (error: any) {
      console.error('Copy schedules error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to copy schedules';
      setErrors({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      source_start_date: '',
      source_end_date: '',
      target_start_date: '',
    });
    setErrors({});
    setCopyResult(null);
    onClose();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog size="lg" maxWidth="md">
        <ModalClose />
        <Typography level="h4" mb={2}>
          Copy Schedules
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {errors.general && (
              <Alert color="danger" variant="soft">
                {errors.general}
              </Alert>
            )}

            <Typography level="body-sm" color="neutral">
              Copy schedules from one timeframe to another. All copied schedules will be created as drafts.
            </Typography>

            <Grid container spacing={2}>
              <Grid xs={6}>
                <FormControl error={!!errors.source_start_date}>
                  <Typography level="body-sm" mb={1}>
                    Source Start Date *
                  </Typography>
                  <Input
                    type="datetime-local"
                    value={formData.source_start_date}
                    onChange={(e) => setFormData({ ...formData, source_start_date: e.target.value })}
                    error={!!errors.source_start_date}
                  />
                  {errors.source_start_date && (
                    <Typography level="body-xs" color="danger" mt={1}>
                      {errors.source_start_date}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={6}>
                <FormControl error={!!errors.source_end_date}>
                  <Typography level="body-sm" mb={1}>
                    Source End Date *
                  </Typography>
                  <Input
                    type="datetime-local"
                    value={formData.source_end_date}
                    onChange={(e) => setFormData({ ...formData, source_end_date: e.target.value })}
                    error={!!errors.source_end_date}
                  />
                  {errors.source_end_date && (
                    <Typography level="body-xs" color="danger" mt={1}>
                      {errors.source_end_date}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <FormControl error={!!errors.target_start_date}>
              <Typography level="body-sm" mb={1}>
                Target Start Date *
              </Typography>
              <Input
                type="datetime-local"
                value={formData.target_start_date}
                onChange={(e) => setFormData({ ...formData, target_start_date: e.target.value })}
                error={!!errors.target_start_date}
              />
              {errors.target_start_date && (
                <Typography level="body-xs" color="danger" mt={1}>
                  {errors.target_start_date}
                </Typography>
              )}
            </FormControl>

            <FormControl>
              <Typography level="body-sm" mb={1}>
                Employee (Optional)
              </Typography>
              <Select
                placeholder="All employees"
                value={formData.employee_id ? String(formData.employee_id) : ''}
                onChange={(_, value) => setFormData({ ...formData, employee_id: value ? Number(value) : undefined })}
              >
                {employees.map((employee) => (
                  <Option key={employee.id} value={String(employee.id)}>
                    {employee.first_name} {employee.last_name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <Typography level="body-sm" mb={1}>
                Shift Type (Optional)
              </Typography>
              <Select
                placeholder="All shift types"
                value={formData.shift_type_id ? String(formData.shift_type_id) : ''}
                onChange={(_, value) => setFormData({ ...formData, shift_type_id: value ? Number(value) : undefined })}
              >
                {shiftTypes.map((shiftType) => (
                  <Option key={shiftType.id} value={String(shiftType.id)}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: shiftType.color,
                        }}
                      />
                      {shiftType.name}
                    </Stack>
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <Typography level="body-sm" mb={1}>
                Status Filter (Optional)
              </Typography>
              <Select
                placeholder="All statuses"
                value={formData.status || ''}
                onChange={(_, value) => setFormData({ ...formData, status: (value as 'draft' | 'published') || undefined })}
              >
                <Option value="draft">Draft</Option>
                <Option value="published">Published</Option>
              </Select>
            </FormControl>

            {copyResult && (
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-lg" mb={2}>
                    Copy Results
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Chip
                        startDecorator={<CheckCircle />}
                        color="success"
                        variant="soft"
                      >
                        {copyResult.copied} copied
                      </Chip>
                      {copyResult.conflicts > 0 && (
                        <Chip
                          startDecorator={<Warning />}
                          color="warning"
                          variant="soft"
                        >
                          {copyResult.conflicts} conflicts
                        </Chip>
                      )}
                    </Stack>

                    {copyResult.details.conflicts.length > 0 && (
                      <Box>
                        <Typography level="body-sm" color="warning" mb={1}>
                          Conflicts found (schedules not copied):
                        </Typography>
                        <Stack spacing={1}>
                          {copyResult.details.conflicts.slice(0, 5).map((conflict, index) => (
                            <Typography key={index} level="body-xs" color="neutral">
                              Employee {conflict.employee_id}: {formatDateTime(conflict.start_datetime)} - {formatDateTime(conflict.end_datetime)}
                            </Typography>
                          ))}
                          {copyResult.details.conflicts.length > 5 && (
                            <Typography level="body-xs" color="neutral">
                              ... and {copyResult.details.conflicts.length - 5} more
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            <Divider />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isLoading}
                startDecorator={<ContentCopy />}
              >
                Copy Schedules
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
} 